import { Breadcrumbs } from "@components/Breadcrumbs"
import { Button, ButtonAnchor } from "@components/Buttons"
import Footer from "@components/Footer"
import { useIntl } from "@components/Intl"
import IntlLink from "@components/Intl/IntlLink"
import { navigation } from "@components/Intl/messages/navigation"
import { Box, Container } from "@components/Layout"
import { Gallery } from "@components/PhotoGallery"
import { ProductUSP } from "@components/ProductUSP"
import SEO from "@components/SEO"
import { InstagramFilterCard } from "@components/Social/InstagramFilterCard"
import { ThisConfiguration } from "@components/ThisConfiguration"
import { Paragraph, ParagraphTitle, TitleLAlt } from "@components/Typography"
import { ChoicesArticleCombination } from "@generated"
import formatPrice from "@lib/formatPrice"
import { getSizeLabel } from "@screens/collection/detail/ArticleGrid/helpers/get-size-label"
import React from "react"
import { ProductType } from "."
import SaveRecentlyViewed from "./SaveRecentlyViewed"
import SeoDescription from "./SeoDescription"
import { ProductVariants } from "./Variants"
import * as S from "./elements"

interface Props {
  breadcrumbs: any[]
  images: string[]
  thumbnails: string[]
  name: string
  material?: string
  articlesId: string[]
  style?: string | null
  price: number | null
  customizeUrl?: string | null
  uid: string
  pid: string
  relatedCombinationId?: string
  slug: string | null
  type: string | null
  productType: ProductType
  properties: { [key: string]: string }
  onAddToCart: () => void
  description?: string | null
  isLoading: boolean
  variants?: ChoicesArticleCombination[]
}

const Detail: React.FC<Props> = ({
  breadcrumbs,
  images,
  thumbnails,
  name,
  material,
  articlesId,
  style,
  price,
  pid,
  uid,
  slug,
  type,
  customizeUrl,
  properties,
  onAddToCart,
  description,
  isLoading,
  variants,
  relatedCombinationId,
}) => {
  const { messages } = useIntl()
  let pathNl: string | null = `/nl${navigation.nl[uid].url}/${slug}-${pid}`
  let pathEn: string | null = `/en${navigation.en[uid].url}/${slug}-${pid}`

  return (
    <>
      <SEO
        title={`${messages.navigation[uid].title} | ${name}`}
        imageUrl={images[0] ? images[0] : ""}
        pathNl={pathNl}
        pathEn={pathEn}
        description={description}
      />
      <SaveRecentlyViewed id={pid} />
      <Container mt={["4rem", "5rem", "6rem"]} py={["3rem", "2rem", "4rem"]}>
        <Box display={["none", "block", "block"]}>
          <Breadcrumbs items={breadcrumbs} />
        </Box>
        <Box
          display="grid"
          gridColumnGap={3}
          gridTemplateColumns={["1fr", "1fr", "1fr 1fr"]}
          py={["4"]}
          borderBottom="1px solid #E5E5E5"
        >
          <Gallery images={images} thumbnails={thumbnails} />
          <Box
            maxWidth={["inherit", "inherit", "28rem"]}
            width="100%"
            ml={["inherit", "inherit", "auto"]}
            py="1rem"
          >
            <Box mb={["2rem", "2rem"]}>
              <TitleLAlt as="h1">{name}</TitleLAlt>
              <S.PriceEl as="h3">{price && formatPrice(price)}</S.PriceEl>
            </Box>
            {variants && variants.length > 0 && (
              <ProductVariants
                items={variants}
                relatedCombinationId={relatedCombinationId}
                uid={uid}
              />
            )}
            <Box
              display="grid"
              gridTemplateColumns={["1fr", "1fr 1fr"]}
              gridColumnGap={[0, "1rem"]}
              gridRowGap={["0.5rem", 0]}
              mb={["2rem", "2rem"]}
            >
              {customizeUrl && (
                <IntlLink href={customizeUrl} as={customizeUrl}>
                  <ButtonAnchor
                    icon="configure"
                    href={customizeUrl}
                    variant="purple"
                    title={messages.global.configure}
                  >
                    {messages.global.configure}
                  </ButtonAnchor>
                </IntlLink>
              )}

              <Button
                variant="purple-border"
                icon="add-to-bag"
                title={messages.global.addToBag}
                onClick={onAddToCart}
                state={isLoading ? "loading" : ""}
              >
                {messages.global.addToBag}
              </Button>
            </Box>
            <ThisConfiguration
              items={Object.keys(properties).map((key) => ({
                key: messages.properties[key].title,
                value:
                  key === "size"
                    ? getSizeLabel(style, properties.size)
                    : messages.properties[key].options[properties[key]],
              }))}
            />
            <Box py="1rem">
              <ProductUSP variant="vertical" />
            </Box>
            {type === "earring" && <InstagramFilterCard />}
          </Box>
        </Box>
        <Box pt="2rem" pb="1rem">
          {!slug?.includes("pendant") && !description ? (
            <SeoDescription
              uid={uid}
              prodName={name}
              material={material}
              articlesId={articlesId}
              colName={style && style}
            />
          ) : (
            <Box position="relative" width="100%">
              <ParagraphTitle>{messages.global.aboutProduct}</ParagraphTitle>
              <Paragraph
                color="text.light"
                style={{ fontSize: "14px", lineHeight: "1.4" }}
              >
                {description}
              </Paragraph>
            </Box>
          )}
        </Box>
      </Container>
      <Footer />
    </>
  )
}

export default Detail
